import { ReactElement } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { Chips } from '@frontend/chips';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Heading, Text, Truncated } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../constants';
import { useGetEntityArchiveStatus } from '../../../hooks';
import { stripDateOrdinals } from '../../../utils';
import { OverlappingEventIndicator } from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { AppointmentTypeChip } from '../../ManagePractitionersV3/appointment-type-chip';
import { EventCardAppointmentStatusChip } from '../../v3/EventCardAppointmentStatusChip';
import { ScheduleEventAppointmentDetails } from '../helpers';
import { AppointmentStatus } from './appointment-status';

const AppointmentDetailItem = ({
  label,
  value,
  showTruncatedText = false,
}: {
  label: string;
  value: string | ReactElement;
  showTruncatedText?: boolean;
}) => {
  const isStringValue = typeof value === 'string';
  return (
    <div css={{ ...(showTruncatedText && { maxWidth: 150 }) }}>
      <Text as='dt' color='light' size='small'>
        {label}
      </Text>
      {!isStringValue && value}
      {isStringValue && showTruncatedText && (
        <Truncated as='dd' css={{ fontWeight: theme.font.weight.bold }}>
          {value || '-'}
        </Truncated>
      )}
      {isStringValue && !showTruncatedText && (
        <Text as='dd' weight='bold'>
          {value || '-'}
        </Text>
      )}
    </div>
  );
};

interface AppointmentDetailsProps {
  appointmentDetails: ScheduleEventAppointmentDetails;
  locationName: string;
  locationId: string;
  appointmentId: string;
  isIntegratedOffice: boolean;
}

export const AppointmentDetails = ({
  appointmentDetails: {
    displayOptions,
    type,
    status,
    date,
    practitioner,
    duration,
    operatory,
    isOverlapping,
    appointmentTypeId,
    providerIds,
    referenceId,
  },
  locationName,
  locationId,
  appointmentId,
  isIntegratedOffice,
}: AppointmentDetailsProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const token = useTranslationTokens();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  const { isAppointmentTypeArchived, isProviderArchived } = useGetEntityArchiveStatus({
    appointmentTypeId: appointmentTypeId || '',
    providerId: providerIds?.[0],
  });

  const showOperatoryItem = isScheduleV3FlagEnabled ? isV3OperatorySupported : isIntegratedOffice;

  const startDate = formatDate(stripDateOrdinals(date), DATETIME_FORMATS.ISO_DATE);

  return (
    <section css={{ marginBottom: theme.spacing(5) }}>
      <header css={headerStyle}>
        <Heading level={2}>{t('Appointment')}</Heading>
        {status &&
          (isScheduleV3FlagEnabled ? (
            <EventCardAppointmentStatusChip
              locationId={locationId}
              appointmentId={appointmentId}
              appointmentStatus={status}
              appointmentStartDate={startDate}
              appointmentReferenceId={referenceId}
            />
          ) : (
            <AppointmentStatus locationId={locationId} appointmentId={appointmentId} status={status} />
          ))}
      </header>
      <dl css={dlStyle(breakpoints.small.max)}>
        <AppointmentDetailItem label={t('Date')} value={date} />
        <AppointmentDetailItem
          label={t('Time')}
          value={
            isScheduleV3FlagEnabled && isOverlapping ? (
              <>
                <Text as='dd' weight='bold' css={{ display: 'inline-block' }}>
                  {duration || '-'}
                </Text>
                <OverlappingEventIndicator variant='full' visible marginLeft={theme.spacing(1)} />
              </>
            ) : (
              duration
            )
          }
        />
        <AppointmentDetailItem
          label={t('Appointment Type')}
          value={
            type ? (
              <AppointmentTypeChip
                as='dd'
                name={type}
                color={displayOptions?.primaryColor}
                maxWidth={156}
                isArchived={isAppointmentTypeArchived}
              />
            ) : (
              <Text as='dd' weight='bold'>
                -
              </Text>
            )
          }
        />
        <AppointmentDetailItem
          label={t('Provider')}
          value={
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
              {isProviderArchived && <Icon name={'archive'} color='subdued' />}
              <Truncated
                as='dd'
                css={{
                  fontWeight: theme.font.weight.bold,
                  color: isProviderArchived ? theme.colors.text.subdued : theme.colors.text.default,
                }}
              >
                {practitioner || '-'}
              </Truncated>
            </div>
          }
          showTruncatedText
        />
        {showOperatoryItem && <AppointmentDetailItem label={token.operatory} value={operatory ?? ''} />}
        <AppointmentDetailItem
          label={t('Location')}
          value={
            <dd>
              <Chips.LocationChip>{locationName}</Chips.LocationChip>
            </dd>
          }
        />
      </dl>
    </section>
  );
};

const headerStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  marginBottom: theme.spacing(3),
});

const dlStyle = (breakpoint = 744) => css`
  display: grid;
  grid-template-columns: 0fr 0fr 1fr;
  grid-column-gap: ${theme.spacing(4)};
  grid-row-gap: ${theme.spacing(3)};
  white-space: nowrap;

  @media (max-width: ${breakpoint}px) {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${theme.spacing(2)};
    grid-row-gap: ${theme.spacing(2)};
    white-space: normal;
  }
`;
